import type {
  CarrierApiPayload,
  Endpoints,
  ForgotApiPayload,
  FullReportPayload,
  GenerateReportPayload,
  GenerateSampleReportPayload,
  GenerateSummaryReportPayload,
  HelpPayload,
  ReportPayload,
  SetupApiPayload,
  SigninApiPayload,
  VerifyApiPayload,
  VisitApiPayload,
} from '../../../types';
import { fetchJson } from './fetchJson';
import { fetchJsonEnhanced } from './fetchJsonEnhanced';

const apiPrefix = '/ws';
const lavaravelPrefix = '/laravel';
const apiLavaravelPrefix = '/laravel/api';
const apiB2cPrefix = '/b2c';

const endpoints: Endpoints = {
  signout: `${lavaravelPrefix}/v1/session/logout`,
  quote: `${apiLavaravelPrefix}/citazione`,
  getSession: `${lavaravelPrefix}/v1/session`,
  generateSummaryReport: ({ code }: GenerateSummaryReportPayload) =>
    `${lavaravelPrefix}/v1/sauton/campione/${code}/riepilogo-referto`,
  signin: ({ user, password }: SigninApiPayload) =>
    `${lavaravelPrefix}/admin/v1/login?login=${user}&pwd=${password}&lang=it`,
  forgot: ({ email, cf }: ForgotApiPayload) => `${apiPrefix}/forgot_password.php?login=${email}&cf=${cf}`,
  help: ({ fullName, email, subject, message }: HelpPayload) =>
    `${apiPrefix}/send_email.php?from=${email}&from_name=${fullName}&subject=${subject}&message=${message}`,
  report: ({ username, patientId }: ReportPayload) =>
    `${apiPrefix}/select_guest.php?pk_id_pt=${patientId}&login=${username}`,
  fullReport: ({ username, patientId }: FullReportPayload) =>
    `${apiPrefix}/select_guest_genetica.php?pk_id_pt=${patientId}&login=${username}`,
  generateReport: ({ code, patientId }: GenerateReportPayload) =>
    `${lavaravelPrefix}/v1/report/merge?pk_id=${patientId}&codice=${code}&tipo=2`,
  generateSampleReport: ({ type, patientId }: GenerateSampleReportPayload) =>
    `${lavaravelPrefix}/v1/report/campione?pk_id_cm=${patientId}&tipo=${type}`,
  forgotSms: ({ email, cf }: ForgotApiPayload) => ({
    url: `${apiLavaravelPrefix}/forgot-password`,
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      Accept: 'application/json',
    },
    body: `email=${email}&fiscal_code=${cf}`,
  }),
  verify: ({ username, token }: VerifyApiPayload) => ({
    url: `${apiLavaravelPrefix}${apiB2cPrefix}/verify`,
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      Accept: 'application/json',
    },
    body: `username=${username}&token=${token}`,
  }),
  visit: ({ visitId, accessToken, payload }: VisitApiPayload) => ({
    url: `${apiLavaravelPrefix}/visita/${visitId}/dato-visita`,
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(payload),
  }),
  pickUpConfirmation: ({ visitId, accessToken, payload }: CarrierApiPayload) => ({
    url: `${apiLavaravelPrefix}/visita/${visitId}/conferma-ritiro`,
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      Authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(payload),
  }),
  setup: ({ pazienteId, secretGdSuiteCode }: SetupApiPayload) => ({
    url: `${apiLavaravelPrefix}${apiB2cPrefix}/setup`,
    method: 'POST',
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
      Accept: 'application/json',
    },
    body: `paziente_id=${pazienteId}&secret_gd_suite_code=${secretGdSuiteCode}`,
  }),
};

const api = {
  signout: async () => await fetchJson(endpoints.signout),
  quote: async () => await fetchJson(endpoints.quote),
  getSession: async () => await fetchJson(endpoints.getSession),
  signin: async (data: SigninApiPayload) => await fetchJson(endpoints.signin(data)),
  forgot: async (data: ForgotApiPayload) => await fetchJson(endpoints.forgot(data)),
  help: async (data: HelpPayload) => await fetchJson(endpoints.help(data)),
  report: async (data: ReportPayload) => await fetchJson(endpoints.report(data)),
  fullReport: async (data: FullReportPayload) => await fetchJson(endpoints.fullReport(data)),
  generateReport: async (data: GenerateReportPayload) => await fetchJson(endpoints.generateReport(data)),
  generateSampleReport: async (data: GenerateSampleReportPayload) =>
    await fetchJson(endpoints.generateSampleReport(data)),
  generateSummaryReport: async (data: GenerateSummaryReportPayload) => endpoints.generateSummaryReport(data),
  forgotSms: async (data: ForgotApiPayload) => await fetchJsonEnhanced(endpoints.forgotSms(data)),
  verify: async (data: VerifyApiPayload) => await fetchJsonEnhanced(endpoints.verify(data)),
  visit: async (data: VisitApiPayload) => await fetchJsonEnhanced(endpoints.visit(data)),
  pickUpConfirmation: async (data: CarrierApiPayload) => await fetchJsonEnhanced(endpoints.pickUpConfirmation(data)),
  setup: async (data: SetupApiPayload) => await fetchJsonEnhanced(endpoints.setup(data)),
};

export default api;
